import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Home, Newspaper, FolderOpen, BarChart3, Route as RouteIcon, Mail, type LucideIcon } from "lucide-react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { CRITICAL_CSS } from "../lib/critical-css";
import { Search, SearchButton } from "@/components/Search";
import { IntroOverlay } from "@/components/IntroOverlay";
import { CookieBanner, CookiePreferencesButton } from "@/components/CookieBanner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useImageProtection } from "@/hooks/use-image-protection";
import { useHasProjets } from "@/hooks/use-has-projets";
import { QueryPersistence } from "@/components/QueryPersistence";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="eyebrow">Erreur 404</p>
        <h1 className="mt-4 font-serif text-6xl">Introuvable</h1>
        <p className="mt-4 text-sm text-muted-foreground">Cette page n’existe pas ou a été déplacée.</p>
        <Link to="/" className="nav-link mt-8 inline-block text-sm tracking-wide micro-interactive active:scale-95">
          Retour à l’accueil
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="eyebrow">Erreur</p>
        <h1 className="mt-4 font-serif text-4xl">Un incident est survenu</h1>
        <p className="mt-3 text-sm text-muted-foreground">Rafraîchissez la page ou revenez à l’accueil.</p>
        <div className="mt-6 flex justify-center gap-4 text-sm">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="border-b border-foreground pb-1"
          >
            Réessayer
          </button>
          <a href="/" className="pb-1">
            Accueil
          </a>
        </div>
      </div>
    </div>
  );
}

const SUPABASE_ORIGIN = "https://onzpbltcttdgsiiyqlwj.supabase.co";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "author", content: "Mathys Gautier" },
      { name: "google-site-verification", content: "haHoEDbHaZ1Yp4y36VoWgEzIDT7Wx4lZJXIu_z8ktpU" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Mathys Gautier" },
      { property: "og:image", content: "https://mathysgautier.com/og-image.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/jpeg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://mathysgautier.com/og-image.jpg" },
    ],

    links: [
      { rel: "preconnect", href: SUPABASE_ORIGIN, crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://static.cloudflareinsights.com" },
      { rel: "dns-prefetch", href: SUPABASE_ORIGIN },
      { rel: "preload", as: "style", href: appCss },
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const THEME_SCRIPT = `(function(){try{var s=localStorage.getItem('mg-theme');var d=s==='dark'||(s!=='light'&&window.matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.classList.toggle('dark',d);}catch(_){}})();`;

/** Bascule la feuille de styles non critique de media="print" vers "all" une fois téléchargée. */
const ASYNC_CSS_SCRIPT = `(function(){var l=document.querySelector('link[data-async-css]');if(!l)return;if(l.sheet){l.media='all';return;}l.addEventListener('load',function(){l.media='all';});})();`;

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="fr" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* Le nonce CSP est ajouté au HTML par le serveur (src/server.ts) après le rendu :
            React ne le connaît pas, d'où `suppressHydrationWarning` pour éviter un
            décalage d'hydratation (et le re-rendu client qui effacerait le nonce). */}
        <style suppressHydrationWarning dangerouslySetInnerHTML={{ __html: CRITICAL_CSS }} />
        <link rel="stylesheet" href={appCss} media="print" data-async-css="" suppressHydrationWarning />
        <noscript>
          <link rel="stylesheet" href={appCss} />
        </noscript>
        <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: THEME_SCRIPT }} />
        <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: ASYNC_CSS_SCRIPT }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

const navItems: ReadonlyArray<{
  to: string;
  label: string;
  icon: LucideIcon;
}> = [
  { to: "/", label: "Accueil", icon: Home },
  { to: "/portfolio", label: "Portfolio", icon: Newspaper },
  { to: "/projets", label: "Projets", icon: FolderOpen },
  { to: "/dataviz", label: "Dataviz", icon: BarChart3 },
  { to: "/parcours", label: "Parcours", icon: RouteIcon },
  { to: "/contact", label: "Contact", icon: Mail },
];

function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const hasProjets = useHasProjets();
  const visibleNavItems = navItems.filter((item) => item.to !== "/projets" || hasProjets);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Desktop: horizontal top nav — visible at the top of the page */}
      <header
        className={`sticky top-4 z-40 hidden px-4 transition-all duration-500 ease-out md:block ${
          scrolled ? "pointer-events-none -translate-y-4 opacity-0" : "translate-y-0 opacity-100"
        }`}
        aria-hidden={scrolled}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-end">
          <nav className="glass flex items-center gap-1 rounded-full px-2 py-1.5 text-sm">
            {visibleNavItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-full px-3.5 py-1.5 text-foreground/70 transition-all duration-300 ease-out hover:text-foreground hover:bg-foreground/5 active:scale-95"
                activeOptions={{ exact: item.to === "/" }}
                activeProps={{
                  className: "text-ocean bg-ocean-50",
                }}
              >
                {item.label}
              </Link>
            ))}
            <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
            <SearchButton />
            <ThemeToggle />
          </nav>
        </div>
      </header>

      {/* Desktop: vertical rail — appears once the user scrolls */}
      <nav
        aria-label="Navigation principale"
        className={`fixed right-4 top-1/2 z-40 hidden -translate-y-1/2 transition-all duration-500 ease-out md:block ${
          scrolled ? "translate-x-0 opacity-100" : "pointer-events-none translate-x-6 opacity-0"
        }`}
      >
        <ul className="glass flex flex-col items-center gap-1 rounded-full p-1.5">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  aria-label={item.label}
                  title={item.label}
                  className="group relative flex h-9 w-9 items-center justify-center rounded-full text-foreground/60 transition-all duration-300 ease-out hover:text-foreground hover:bg-foreground/5 active:scale-90"
                  activeOptions={{ exact: item.to === "/" }}
                  activeProps={{
                    className: "text-ocean bg-ocean-50",
                  }}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                  <span className="pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-full glass px-3 py-1 text-xs opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0">
                    {item.label}
                  </span>
                </Link>
              </li>
            );
          })}
          <li className="my-0.5 h-px w-5 bg-border" aria-hidden="true" />
          <li>
            <SearchButton />
          </li>
          <li>
            <ThemeToggle />
          </li>
        </ul>
      </nav>

      {/* Mobile: compact liquid-glass dock fixed at the bottom */}
      <nav
        aria-label="Navigation principale"
        className="fixed right-0 bottom-0 left-0 z-50 pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <div className="mx-4 mb-4 flex h-[60px] items-center justify-around rounded-2xl glass shadow-lg">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-label={item.label}
                className="flex h-11 w-11 items-center justify-center rounded-full text-foreground/60 transition-all duration-200 ease-out hover:text-foreground hover:bg-foreground/5 active:scale-90"
                activeOptions={{ exact: item.to === "/" }}
                activeProps={{
                  className: "text-ocean bg-ocean-50",
                }}
              >
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </Link>
            );
          })}
          <SearchButton className="h-11 w-11" />
          <ThemeToggle className="h-11 w-11" />
        </div>
      </nav>
    </>
  );
}

function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-border">
      <div className="mx-auto max-w-6xl px-6 pt-10 pb-6">
        <p className="max-w-3xl text-xs leading-relaxed text-muted-foreground">
          N’ayant pas l’ambition de « maîtriser » le CSS et le JavaScript — à la différence de R, Python ou encore des
          logiciels de tableur — ce site a été créé à partir de l’intelligence artificielle (Lovable) et d’une série de
          prompts.
        </p>

        <div className="my-6 border-t border-border" />

        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <p className="font-serif text-3xl leading-none tracking-tight">
              <span className="font-light text-foreground/70">Mathys</span>{" "}
              <em className="font-semibold not-italic text-ocean">Gautier</em>
            </p>
          </div>
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
            INVESTIGATION · DATA · VISUALISATION
          </p>
        </div>
        <div className="mt-6 flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <nav aria-label="Liens légaux" className="flex flex-wrap items-center gap-4">
            <Link to="/mentions-legales" className="no-link-underline">Mentions légales</Link>
            <Link to="/confidentialite" className="no-link-underline">Politique de confidentialité</Link>
            <CookiePreferencesButton className="underline underline-offset-4 transition-colors hover:text-foreground" />
          </nav>
          <p>© {new Date().getFullYear()} — Tous droits réservés</p>
        </div>
      </div>
    </footer>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hideChrome = pathname.startsWith("/redaction");
  useImageProtection();

  return (
    <QueryClientProvider client={queryClient}>
      <QueryPersistence />
      {pathname === "/" && <IntroOverlay />}
      <Search />
      <CookieBanner />

      <div className="flex min-h-screen flex-col">
        <a
          href="#contenu"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-foreground focus:px-4 focus:py-2 focus:text-background"
        >
          Aller au contenu principal
        </a>
        {!hideChrome && <SiteHeader />}
        <main id="contenu" className={`flex-1 ${hideChrome ? "" : "pb-24 md:pb-0"}`}>
          <Outlet />
        </main>
        {!hideChrome && <SiteFooter />}
      </div>
      <Toaster />
    </QueryClientProvider>
  );
}
